import React from "react";
import {
  Card,
  CardContent,
  Chip,
  Box,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha, useTheme } from "@mui/material/styles";
import { useColorMode } from "../../../context/ColorModeContext";
import {
  Visibility,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HourglassEmpty as PendingIcon,
  PendingActions as PendingActionsIcon,
  CheckCircleOutline as SignedIcon,
  CloudDone as SubmittedIcon,
  ReportProblemOutlined as ChangesRequestedIcon,
  VerifiedOutlined as ApprovedIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { ReportGenReport } from "../../../shared/interfaces/ReportGenReport";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { NavigateFunction } from "react-router-dom";
import {
  AWAITING_INPUT_BASE,
  getAwaitingInputChipSx,
  getAwaitingInputSurfaceStyles,
} from "../../../utils/awaitingInputStyles";
import {
  getAwaitingInputActionLabel,
  getAwaitingInputRoute,
} from "../../../features/hitl/presentPlan";
import { useHitlEnabled } from "../../../hooks/useHitlEnabled";
import { useCoApprovalEnabled } from "../../../hooks/useCoApprovalEnabled";
import { usePermissions } from "../../../hooks/usePermissions";
import { REPORTS_MANAGE_STATION_PERMISSION } from "../../../shared/auth/authorization";
import { reportStatusLabel } from "../../../shared/reportStatusDisplay";

interface ReportCardProps {
  report: ReportGenReport;
  navigate: NavigateFunction;
  onDelete: (report: ReportGenReport) => void;
}

// Reports the officer can no longer edit (display only).
const READ_ONLY_STATUSES = [
  ReportStatus.CO_APPROVED,
  ReportStatus.SIGNED,
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.RMS_COMMITTED,
];

// Display grouping for the shared "submitted" visual accent + icon on the Home
// list: both an officer-signed SIGNED report and an IN_CO_REVIEW report get the
// same "info" treatment. This is PURELY cosmetic. The CO-actionable label,
// routing, and action button are gated on IN_CO_REVIEW specifically — a SIGNED
// report is a completed non-CO sign-off that never enters the CO queue, so it is
// never labeled "NEEDS REVIEW" nor routed to /co-review.
const isSubmittedForReview = (s: ReportStatus) =>
  s === ReportStatus.SIGNED || s === ReportStatus.IN_CO_REVIEW;

export const ReportCard: React.FC<ReportCardProps> = ({
  report,
  navigate,
  onDelete,
}) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDark = mode === "dark";
  const hitlEnabled = useHitlEnabled();
  const coApprovalEnabled = useCoApprovalEnabled();
  const permissions = usePermissions();
  const isCoView = permissions.includes(REPORTS_MANAGE_STATION_PERMISSION);
  const status = report.status as ReportStatus;

  // With the flag off, a CO-track report (IN CO REVIEW / CHANGES REQUESTED)
  // keeps its stored status and is frozen until the flag returns — it can't be
  // edited, re-signed, or reviewed. Present it read-only so the owner isn't
  // dropped into an editor whose save is disabled server-side.
  const isFrozenCoTrack =
    !coApprovalEnabled &&
    (status === ReportStatus.IN_CO_REVIEW ||
      status === ReportStatus.CHANGES_REQUESTED);

  const isAwaitingInput = status === ReportStatus.AWAITING_INPUT;
  const awaitingInputSurfaceStyles = getAwaitingInputSurfaceStyles(theme);
  const awaitingInputRoute = getAwaitingInputRoute(report.id, hitlEnabled);
  const awaitingInputActionLabel = getAwaitingInputActionLabel(hitlEnabled);

  const targetRoute = (() => {
    if (isFrozenCoTrack) return `/preview-report-info/${report.id}`;
    if (status === ReportStatus.PENDING_REVIEW) return `/edit-section/${report.id}`;
    if (status === ReportStatus.AWAITING_INPUT) return awaitingInputRoute;
    if (status === ReportStatus.CHANGES_REQUESTED) {
      // Officer fixes; CO previewing.
      return isCoView
        ? `/preview-report-info/${report.id}`
        : `/edit-section/${report.id}`;
    }
    if (status === ReportStatus.IN_CO_REVIEW && isCoView) return `/co-review/${report.id}`;
    if (status === ReportStatus.CO_APPROVED && isCoView) return `/co-review/${report.id}`;
    return `/preview-report-info/${report.id}`;
  })();

  const accent = (() => {
    if (status === ReportStatus.PENDING_REVIEW) return theme.palette.warning.main;
    if (status === ReportStatus.AWAITING_INPUT) return AWAITING_INPUT_BASE;
    if (isSubmittedForReview(status)) return theme.palette.info.main;
    if (status === ReportStatus.CHANGES_REQUESTED) return theme.palette.error.main;
    if (status === ReportStatus.CO_APPROVED) return theme.palette.success.main;
    if (status === ReportStatus.RMS_COMMITTED) return theme.palette.success.main;
    return theme.palette.primary.main;
  })();

  const chipLabel = reportStatusLabel(status, isCoView);

  const chipIcon = (() => {
    const iconSx = { fontSize: "0.9rem !important" } as const;
    if (status === ReportStatus.PENDING_REVIEW) return <PendingIcon sx={iconSx} />;
    if (status === ReportStatus.AWAITING_INPUT) return <PendingActionsIcon sx={iconSx} />;
    if (isSubmittedForReview(status)) return <SignedIcon sx={iconSx} />;
    if (status === ReportStatus.CHANGES_REQUESTED)
      return <ChangesRequestedIcon sx={iconSx} />;
    if (status === ReportStatus.CO_APPROVED) return <ApprovedIcon sx={iconSx} />;
    if (status === ReportStatus.RMS_COMMITTED) return <SubmittedIcon sx={iconSx} />;
    return undefined;
  })();

  const chipSx = (() => {
    if (status === ReportStatus.AWAITING_INPUT) {
      return getAwaitingInputChipSx();
    }
    return {
      backgroundColor: alpha(accent, 0.15),
      color: accent,
      "& .MuiChip-icon": { color: "inherit" },
    } as const;
  })();

  const isReadOnly =
    (READ_ONLY_STATUSES.includes(status) && !isCoView) || isFrozenCoTrack;
  // CO viewers can act on signed/approved reports, so don't treat them as read-only buttons.
  const isApprovedAesthetic = status === ReportStatus.CO_APPROVED || status === ReportStatus.RMS_COMMITTED;
  const showDeleteButton = !isReadOnly && !isApprovedAesthetic;
  const actionTooltip = (() => {
    if (isFrozenCoTrack) return "View Report";
    if (status === ReportStatus.AWAITING_INPUT) return awaitingInputActionLabel;
    if (status === ReportStatus.CHANGES_REQUESTED && !isCoView) return "Address changes";
    if (status === ReportStatus.SIGNED) return "View Report";
    if (status === ReportStatus.IN_CO_REVIEW && isCoView) return "Review report";
    if (status === ReportStatus.CO_APPROVED && isCoView) return "Commit / download";
    if (isApprovedAesthetic) return "View Report";
    return "Edit Report";
  })();

  const actionIcon = (() => {
    if (isFrozenCoTrack) return <Visibility fontSize="small" />;
    if (status === ReportStatus.AWAITING_INPUT)
      return <PendingActionsIcon fontSize="small" />;
    if (isApprovedAesthetic && !isCoView) return <Visibility fontSize="small" />;
    if (status === ReportStatus.SIGNED) return <Visibility fontSize="small" />;
    if (status === ReportStatus.IN_CO_REVIEW && isCoView)
      return <SignedIcon fontSize="small" />;
    if (status === ReportStatus.CO_APPROVED && isCoView)
      return <ApprovedIcon fontSize="small" />;
    if (status === ReportStatus.CHANGES_REQUESTED && !isCoView)
      return <EditIcon fontSize="small" />;
    return <EditIcon fontSize="small" />;
  })();

  return (
    <Card
      variant="outlined"
      sx={{
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        borderRadius: 2,
        borderColor: isDark
          ? alpha(theme.palette.text.primary, 0.2)
          : alpha(theme.palette.divider, 0.4),
        ...(isAwaitingInput && {
          backgroundColor: awaitingInputSurfaceStyles.backgroundColor,
          borderColor: awaitingInputSurfaceStyles.borderColor,
        }),
        "&:hover": {
          boxShadow: isAwaitingInput
            ? `0 8px 25px ${alpha(AWAITING_INPUT_BASE, 0.18)}`
            : `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
          borderColor: isAwaitingInput
            ? awaitingInputSurfaceStyles.borderColor
            : theme.palette.primary.main,
          transform: "translateY(-3px)",
          "& .card-actions": {
            opacity: 1,
          },
        },
        "&:active": {
          transform: "translateY(-1px)",
        },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderRadius: "4px 0 0 4px",
          bgcolor: accent,
        },
      }}
      onClick={() => navigate(targetRoute)}
    >
      <CardContent sx={{ p: 1.5, pl: 2.5, "&:last-child": { pb: 1.5 } }}>
        <Grid container spacing={2} alignItems="center">
          {/* Main Info Section */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Chip
                icon={chipIcon}
                label={chipLabel}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 26,
                  borderRadius: 1.5,
                  ...chipSx,
                }}
              />
            </Box>

            <Tooltip
              title={report.details || "No details available"}
              placement="bottom-start"
              enterDelay={500}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: 600,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                  color: "text.primary",
                }}
              >
                {report.details || (
                  <Box
                    component="span"
                    sx={{
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    No details available
                  </Box>
                )}
              </Typography>
            </Tooltip>
          </Grid>

          {/* Reported By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.grey[500], isDark ? 0.15 : 0.08),
                transition: "background-color 0.2s ease",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Reported By
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={report.reportedByName}
                sx={{
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {report.reportedByName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ fontSize: "0.75rem" }}
              >
                {report.reportedByPosition}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  mt: 0.25,
                }}
              >
                {dayjs(report.reportedByDate).format("MMM D, YYYY · h:mm A")}
              </Typography>
            </Box>
          </Grid>

          {/* Modified By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.grey[500], isDark ? 0.15 : 0.08),
                transition: "background-color 0.2s ease",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Last Modified
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={report.modifiedByName}
                sx={{
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {report.modifiedByName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  mt: 0.25,
                }}
              >
                {dayjs(report.dateModified).format("MMM D, YYYY · h:mm A")}
              </Typography>
            </Box>
          </Grid>

          {/* Actions Section */}
          <Grid size={{ xs: 12, md: 1 }}>
            {/* Desktop View */}
            <Box
              className="card-actions"
              display={{ xs: "none", md: "flex" }}
              flexDirection="column"
              justifyContent="center"
              gap={1}
              onClick={(e) => e.stopPropagation()}
              sx={{
                opacity: 0.7,
                transition: "opacity 0.2s ease",
              }}
            >
              <Tooltip title={actionTooltip} placement="left">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(targetRoute);
                  }}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                      transform: "scale(1.1)",
                    },
                    width: 36,
                    height: 36,
                  }}
                >
                  {actionIcon}
                </IconButton>
              </Tooltip>
              {showDeleteButton && (
                <Tooltip title="Delete Report" placement="left">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(report);
                    }}
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: "error.main",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                      width: 36,
                      height: 36,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Mobile View */}
            <Box
              display={{ xs: "flex", md: "none" }}
              justifyContent="flex-end"
              gap={1}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(targetRoute);
                }}
              >
                {actionIcon}
              </IconButton>
              {showDeleteButton && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(report);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
